import { forwardRef } from 'react';

export type ButtonProps = {
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  'aria-keyshortcuts'?: string;
};

const BASE =
  'inline-flex items-center justify-center gap-sp-2 rounded-r-default px-sp-4 py-sp-2 text-fs-body font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info disabled:cursor-not-allowed';

/**
 * Filled — exactly one per screen (the >50% action). Strong visual
 * weight; the contract is "this is the thing the user came to do".
 */
export const PrimaryButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function PrimaryButton(
    { label, icon, disabled = false, loading = false, onClick, type = 'button', ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={`${BASE} bg-gray-900 text-gray-50 hover:bg-gray-700 disabled:bg-gray-300 disabled:text-gray-500`}
        {...rest}
      >
        {loading ? <Spinner /> : icon}
        <span>{label}</span>
      </button>
    );
  },
);

/** Outlined secondary action — paired with PrimaryButton. */
export const SecondaryButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function SecondaryButton(
    { label, icon, disabled = false, loading = false, onClick, type = 'button', ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={`${BASE} border border-gray-900 bg-transparent text-gray-900 hover:bg-gray-100 disabled:border-gray-300 disabled:text-gray-500`}
        {...rest}
      >
        {loading ? <Spinner /> : icon}
        <span>{label}</span>
      </button>
    );
  },
);

/** Inline text action — "Snooze 24h", "Analyze ↗". */
export const TextButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function TextButton(
    { label, icon, disabled = false, loading = false, onClick, type = 'button', ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={
          'inline-flex items-center gap-sp-1 text-fs-body font-medium text-info hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-info disabled:cursor-not-allowed disabled:text-gray-500'
        }
        {...rest}
      >
        {loading ? <Spinner /> : icon}
        <span>{label}</span>
      </button>
    );
  },
);

function Spinner() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden
      className="animate-spin"
    >
      <circle
        cx="7"
        cy="7"
        r="5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="20 12"
      />
    </svg>
  );
}
